import {SsoDataValidationStatus} from 'src/openapi/admin/models/SsoDataValidationStatus';
import {SsoStep} from 'src/openapi/admin/models/SsoStep';
import {SupportedSsoProvider} from 'src/openapi/admin/models/SupportedSsoProvider';

import {SSO_STORAGE_PREFIX, readFromStorage} from '../hooks/use-sso-session-storage';

import {getSelectedDomains} from './domain-validation';


import type {VerifyDomainData} from './domain-validation';
import type {ConnectionSettingsResponse} from 'src/openapi/admin/models/ConnectionSettingsResponse';
import type {DraftConnectionResponse} from 'src/openapi/admin/models/DraftConnectionResponse';
import type {UpdateDraftConnectionRequest} from 'src/openapi/admin/models/UpdateDraftConnectionRequest';

const PROVIDER_LOCAL_TO_ENUM: Record<string, SupportedSsoProvider> = {
    okta: SupportedSsoProvider.OKTA,
    microsoft: SupportedSsoProvider.MICROSOFT_ENTRA,
    onelogin: SupportedSsoProvider.ONE_LOGIN,
    google: SupportedSsoProvider.GOOGLE_WORKSPACE,
    pingfederate: SupportedSsoProvider.PING_FEDERATE,
    other: SupportedSsoProvider.OTHER,
};

const PROVIDER_ENUM_TO_LOCAL: Record<string, string> = Object.fromEntries(
    Object.entries(PROVIDER_LOCAL_TO_ENUM).map(([local, enumVal]) => [enumVal, local]),
);

const SSO_STEP_ORDER: SsoStep[] = [
    SsoStep.START_STEP,
    SsoStep.SELECT_IDP,
    SsoStep.VERIFY_DOMAIN,
    SsoStep.SP_METADATA,
    SsoStep.USER_ATTRIBUTES,
    SsoStep.IDP_METADATA,
    SsoStep.TEST_CONNECTION,
    SsoStep.REVIEW_AND_ENFORCE,
];

export function mapLocalIdToSsoProvider(localId: string | null): SupportedSsoProvider | undefined {
    if (localId == null) {
        return undefined;
    }
    return PROVIDER_LOCAL_TO_ENUM[localId];
}

export function mapSsoProviderToLocalId(provider: SupportedSsoProvider | undefined): string | null {
    if (provider == null) {
        return null;
    }
    return PROVIDER_ENUM_TO_LOCAL[provider] ?? null;
}

export function getStepIndexFromSsoStep(ssoStep: SsoStep): number {
    return SSO_STEP_ORDER.indexOf(ssoStep);
}

export function getSsoStepFromIndex(index: number): SsoStep {
    return SSO_STEP_ORDER[index];
}

function writeToStorage(key: string, value: unknown): void {
    try {
        window.sessionStorage.setItem(SSO_STORAGE_PREFIX + key, JSON.stringify(value));
    } catch {
        // sessionStorage unavailable
    }
}

export function removeDeselectedDomainsFromStorage(): void {
    const domainData = readFromStorage<VerifyDomainData>('verifyDomain', {domains: []});
    const selected = getSelectedDomains(domainData.domains);
    if (selected.length === domainData.domains.length) {
        return;
    }
    writeToStorage('verifyDomain', {...domainData, domains: selected});
}

export function isDomainStep(step: SsoStep): boolean {
    return step === SsoStep.VERIFY_DOMAIN || step === SsoStep.TEST_CONNECTION;
}

function isStepReached(latestStep: SsoStep, targetStep: SsoStep): boolean {
    return SSO_STEP_ORDER.indexOf(latestStep) >= SSO_STEP_ORDER.indexOf(targetStep);
}

export function populateSessionStorageFromDraft(
    draft: DraftConnectionResponse,
    connection?: ConnectionSettingsResponse | null,
): void {
    const {latestStep} = draft;

    if (isStepReached(latestStep, SsoStep.SELECT_IDP) && draft.provider != null) {
        writeToStorage('selectedProvider', mapSsoProviderToLocalId(draft.provider));
    }

    if (isStepReached(latestStep, SsoStep.VERIFY_DOMAIN) && draft.domains != null) {
        const verifyDomainData: VerifyDomainData = {
            domains: draft.domains.map(d => ({
                value: d,
                verified: draft.domainVerificationStatus === SsoDataValidationStatus.VALID,
            })),
        };
        writeToStorage('verifyDomain', verifyDomainData);
    }

    if (isStepReached(latestStep, SsoStep.SP_METADATA)) {
        writeToStorage('downloadSpMetadata', {entityId: '', acsUrl: '', xmlFile: ''});
    }

    if (isStepReached(latestStep, SsoStep.USER_ATTRIBUTES)) {
        writeToStorage('confirmLoginFields', draft.loginFieldsConfirmed ?? true);
    }

    if (isStepReached(latestStep, SsoStep.IDP_METADATA)) {
        const storedUploadMetadata = getValidUploadMetadataFromStorage();
        const connectionUploadMetadata = getUploadMetadataFromConnection(connection);
        writeToStorage('uploadMetadata', storedUploadMetadata ?? connectionUploadMetadata ?? {
            method: 'manual' as const,
            url: {url: '', loginUrl: '', certificate: '', valid: false},
            file: {fileName: null, content: null, loginUrl: '', certificate: '', valid: false},
            manual: {loginUrl: '', certificate: '', valid: false},
        });
    }

    if (isStepReached(latestStep, SsoStep.TEST_CONNECTION)) {
        writeToStorage('testEndUser', {confirmed: draft.testConfirmed ?? false});
    }

    if (isStepReached(latestStep, SsoStep.REVIEW_AND_ENFORCE)) {
        writeToStorage('reviewAndEnable', {
            isSsoMandatory: draft.ssoMandatory ?? false,
            saved: false,
        });
    }
}

type UploadMetadataTab = {
    loginUrl: string,
    certificate: string,
    valid?: boolean,
    url?: string,
    fileName?: string | null,
    content?: string | null,
};

type UploadMetadataStorage = {
    method: 'url' | 'file' | 'manual',
    url: UploadMetadataTab,
    file: UploadMetadataTab,
    manual: UploadMetadataTab,
};

function hasUploadMetadataValues(tab: UploadMetadataTab | undefined): tab is UploadMetadataTab {
    return tab?.loginUrl != null
        && tab.loginUrl.length > 0
        && tab.certificate != null
        && tab.certificate.length > 0;
}

function getValidUploadMetadataFromStorage(): UploadMetadataStorage | null {
    const metadataData = readFromStorage<UploadMetadataStorage | null>('uploadMetadata', null);
    if (metadataData == null) {
        return null;
    }

    const selectedTab = metadataData[metadataData.method];
    if (selectedTab?.valid === true && hasUploadMetadataValues(selectedTab)) {
        return metadataData;
    }

    const validTab = [metadataData.url, metadataData.file, metadataData.manual]
        .find(tab => tab.valid === true && hasUploadMetadataValues(tab));
    if (validTab == null) {
        return null;
    }

    return {
        ...metadataData,
        method: 'manual',
        manual: {
            loginUrl: validTab.loginUrl,
            certificate: validTab.certificate,
            valid: true,
        },
    };
}

function getUploadMetadataFromConnection(
    connection: ConnectionSettingsResponse | null | undefined,
): UploadMetadataStorage | null {
    if (connection?.ssoUrl == null || connection.certificate == null) {
        return null;
    }

    if (connection.ssoUrl.length === 0 || connection.certificate.length === 0) {
        return null;
    }

    return {
        method: 'manual',
        url: {url: '', loginUrl: '', certificate: '', valid: false},
        file: {fileName: null, content: null, loginUrl: '', certificate: '', valid: false},
        manual: {
            loginUrl: connection.ssoUrl,
            certificate: connection.certificate,
            valid: true,
        },
    };
}

export function buildUpdateRequestForStep(stepIndex: number): UpdateDraftConnectionRequest {
    const ssoStep = getSsoStepFromIndex(stepIndex);
    const base: UpdateDraftConnectionRequest = {step: ssoStep};

    switch (ssoStep) {
        case SsoStep.SELECT_IDP: {
            const provider = readFromStorage<string | null>('selectedProvider', null);
            return {...base, provider: mapLocalIdToSsoProvider(provider)};
        }
        case SsoStep.REVIEW_AND_ENFORCE: {
            const ssoMandatory = readFromStorage('reviewAndEnable', {isSsoMandatory: true});
            return {...base, ssoMandatory: ssoMandatory.isSsoMandatory};
        }
        case SsoStep.VERIFY_DOMAIN:
        case SsoStep.TEST_CONNECTION: {
            const domainData = readFromStorage<VerifyDomainData>('verifyDomain', {domains: []});
            return {
                ...base,
                domains: getSelectedDomains(domainData.domains).filter(d => d.verified).map(d => d.value),
            };
        }
        case SsoStep.IDP_METADATA: {
            const metadataData = getValidUploadMetadataFromStorage();
            if (metadataData != null) {
                const tab = metadataData[metadataData.method];
                return {
                    ...base,
                    metadata: tab.loginUrl,
                    certificate: tab.certificate,
                };
            }
            return base;
        }
        default:
            return base;
    }
}
